<template>
  <div class="file-input-wrapper">
    <v-icon
      class="file-input-icon"
      @click="triggerFileInput"
    >
      mdi-paperclip
    </v-icon>

    <v-file-input
      ref="fileInputRef"
      variant="outlined"
      density="compact"
      prepend-icon=""
      :label="label"
      :multiple="multiple"
      :show-size="true"
      hide-details
      :disabled="disabled"
      :model-value="modelValue"
      @update:modelValue="onFilesSelected"
      @click:clear="emit('update:modelValue', [])"
      class="flex-grow-1"
    >
      <template #selection="{ fileNames }">
        <v-chip
          v-if="fileNames.length"
          size="small"
          label
          color="primary"
        >
          {{ fileNames.length }} {{ $t('files selected') }}
        </v-chip>
      </template>
    </v-file-input>
  </div>
</template>

<script lang="ts" setup>
import {PropType, ref} from "vue";
import {VFileInput} from "vuetify/components";

const props = defineProps({
  modelValue: { type: Array as PropType<File[]>, default: () => [], required: false },
  title: {type: String, default: ""},
  label: {type: String, default: ""},
  show: {type: Boolean, default: false},
  disabled: {type: Boolean, default: false},
  multiple: {type: Boolean, default: false},
});

const emit = defineEmits<{
  (e: 'dataType', value: boolean): void;
  (e: 'add-files', value: File[]): void;
  (e: 'update:modelValue', value: File[]): void;
  (e: 'invalid-files', files: File[]): void;
}>();

const FILE_PATTERN =
  /\.(avi|wmv|mpg|mpeg|mp4|mov|mjpeg|bmp|gif|jpg|jpeg|png|tif|tiff|heif|heic|pdf)$/i;

const accept =
  '.avi,.wmv,.mpg,.mpeg,.mp4,.mov,.mjpeg,.bmp,.gif,.jpg,.jpeg,.png,.tif,.tiff,.heif,.heic,.pdf';

const fileInputRef = ref<InstanceType<typeof VFileInput> | null>(null)

function triggerFileInput() {
  if (props.disabled) return
  const input = fileInputRef.value?.$el?.querySelector('input[type="file"]')
  if (input) input.click()
}

function onFilesSelected(files: File | File[] | null) {
  const fileArray = Array.isArray(files) ? files : (files ? [files] : []);

  const valid: File[] = [];
  const invalid: File[] = [];

  fileArray.forEach(f =>
    FILE_PATTERN.test(f.name) ? valid.push(f) : invalid.push(f)
  );

  if (valid.length) {
    emit('add-files', valid);
    emit('update:modelValue', valid);
  } else {
    emit('update:modelValue', []);
  }

  if (invalid.length) emit('invalid-files', invalid);
}
</script>

<style lang="scss" scoped>
.file-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;

  .v-input {
    min-height: 56px;
    flex: 1;
  }

  :deep(.file-input-icon) {
    position: absolute;
    inset-inline-start: -2.5rem;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    z-index: 2;
    color: rgb(var(--v-theme-black), var(--v-medium-emphasis-opacity));
  }

  .v-field--variant-outlined .v-field__input {
    background-color: rgb(var(--v-theme-secondary));
    padding-left: 2.5rem;
  }

  .v-input--focused .v-field__input,
  .v-input.v-input--dirty .v-field__input {
    background-color: rgb(var(--v-theme-white));
  }

  :deep(.v-field__input) {
    .v-field-label:not(.v-field-label--floating) {
      display: none !important;
    }
  }

  .comment-input {
    border: none;
    border-bottom: 1px dashed #ccc;
    outline: none;
    font-size: 1rem;
    background: transparent;

    &:focus {
      border-bottom: 1px solid var(--v-theme-primary);
    }
  }
}
</style>
